import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next/src';
import { connect } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import { RaisedButton } from '@hobs/components/Buttons';

import * as Yup from 'yup';
import Dialog from '@hobs/components/Dialog';
import Alert from '@hobs/components/Alert';
import Input from '@hobs/components/Input';
import Loader from '@hobs/components/Loader';
import Textarea from '@hobs/components/Textarea';
import Toast from '@hobs/components/Toast';

import {
  createSubcontractor,
  clearSubcontractor,
  getSubcontractor,
  editSubcontractor
} from '@hobs/actions/subcontractor';

import './SubcontractorDialog.scss';
import Switch from '@hobs/components/Switch';

class SubcontractorDialog extends React.PureComponent {
  subcontractorSchema = null;
  state = {
    isLoading: false,
    error: false
  };

  constructor(props) {
    super(props);

    const { t } = props;
    this.subcontractorSchema = Yup.object().shape({
      organizationCode: Yup.string()
        .max(256, t('Organization code cannot be longer than 256 symbols'))
        .required(t('Organization code is required')),
      organizationName: Yup.string()
        .max(256, t('Organization name cannot be longer than 256 symbols'))
        .required(t('Organization name is required')),
      address: Yup.string().max(
        128,
        t('Address cannot be longer than 128 symbols')
      ),
      email: Yup.string()
        .max(128, t('Email cannot be longer than 128 symbols'))
        .required(t('Email is required'))
        .email(t('Invalid email format')),
      phone: Yup.string().max(
        128,
        t('Phone cannot be longer than 128 symbols')
      ),
      notes: Yup.string().max(500, t('Notes cannot be longer than 500 symbols'))
    });
  }

  getSubcontractorId = () => {
    return this.props.match.params.subcontractorId;
  };

  getSubontractorsPathName() {
    const path = this.props.location.pathname;
    const itemId = this.getSubcontractorId();
    return itemId
      ? path.replace('/edit/' + itemId, '')
      : path.replace('/create', '');
  }

  isDialogOpen() {
    return (
      this.props.match.params.action === 'create' ||
      this.props.match.params.action === 'edit'
    );
  }

  handleSubmit = (values, { setSubmitting, setErrors }) => {
    const itemId = this.getSubcontractorId();
    if (itemId) {
      this.editSubcontractor(values, setSubmitting, setErrors);
    } else {
      this.createSubcontractor(values, setSubmitting, setErrors);
    }
  };

  createSubcontractor = (values, setSubmitting, setErrors) => {
    const { t } = this.props;
    const body = { ...values };
    this.props.createSubcontractor(body).then(response => {
      if (response.success) {
        this.setState({ error: false });
        Toast.success(t('Subcontractor created successfully'));
        this.props.history.push(this.getSubontractorsPathName());
        this.props.clearSubcontractor();
        this.props.onSuccess();
      } else {
        const details = response.payload.details;
        let error = '';
        if (details && details.length > 0) {
          setErrors({
            ...details.reduce((results, item) => {
              results[item.target] = item.message;
              return results;
            }, {})
          });
        } else {
          error = response.payload.message;
          this.setState({ error });
        }
      }
      setSubmitting(false);
    });
  };

  editSubcontractor = (values, setSubmitting, setErrors) => {
    const { t } = this.props;
    const subcontractorId = this.getSubcontractorId();
    const body = {
      subcontractorId,
      ...values
    };
    this.props.editSubcontractor(body).then(response => {
      if (response.success) {
        this.setState({ error: false });
        Toast.success(t('Subcontractor updated successfully'));
        this.props.history.push(this.getSubontractorsPathName());
        this.props.clearSubcontractor();
        this.props.onSuccess();
      } else {
        const details = response.payload.details;
        let error = '';
        if (details && details.length > 0) {
          setErrors({
            ...details.reduce((results, item) => {
              results[item.target] = item.message;
              return results;
            }, {})
          });
        } else {
          error = response.payload.message;
          this.setState({ error });
        }
      }
      setSubmitting(false);
    });
  };

  onDialogOpening = async () => {
    const subcontractorId = this.getSubcontractorId();

    if (subcontractorId) {
      this.setState({ isLoading: true });
      await this.props.getSubcontractor(subcontractorId);
      this.setState({ isLoading: false });
    }
  };

  onDialogClose = () => {
    const itemId = this.getSubcontractorId();
    if (itemId) {
      this.props.clearSubcontractor();
    }

    this.setState({ error: false });
    this.props.history.push(this.getSubontractorsPathName());
  };

  canRenderForm = () => {
    const { subcontractor } = this.props;
    const subcontractorNotEmpty = Object.keys(subcontractor).length > 0;
    const itemId = this.getSubcontractorId();
    return itemId ? subcontractorNotEmpty : true;
  };

  getInitialValues() {
    const { subcontractor } = this.props;

    return {
      isTransportationSupplier: subcontractor.isTransportationSupplier,
      address: subcontractor.address ? subcontractor.address : '',
      email: subcontractor.email ? subcontractor.email : '',
      organizationName: subcontractor.name ? subcontractor.name : '',
      notes: subcontractor.notes ? subcontractor.notes : '',
      organizationCode: subcontractor.organization
        ? subcontractor.organization
        : '',
      phone: subcontractor.phone ? subcontractor.phone : '',
      isActive:
        Object.keys(subcontractor).length === 0 ? true : subcontractor.isActive
    };
  }

  render() {
    const { t } = this.props;
    const { isLoading, error } = this.state;

    return (
      <Dialog
        title={
          this.getSubcontractorId()
            ? t('Edit subcontractor')
            : t('Add new subcontractor')
        }
        isOpen={this.isDialogOpen()}
        enableReinitialize={true}
        onClose={this.onDialogClose}
        onOpening={this.onDialogOpening}
        className="subcontractor-item-create"
      >
        {isLoading && <Loader />}
        {error && <Alert type="error">{t(error)}</Alert>}

        {this.canRenderForm() && !isLoading && (
          <Formik
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={this.subcontractorSchema}
            initialValues={this.getInitialValues()}
            onSubmit={this.handleSubmit}
          >
            {({ isSubmitting, values, setFieldValue }) => (
              <Form>
                <div className="subcontractor-item-create--dialog">
                  <div className="subcontractor-item-create--dialog--form">
                    <div className="subcontractor-item-create--dialog--row">
                      <div className="subcontractor-item-create--dialog--column">
                        <Field name="organizationCode" component={Input}>
                          {t('Organization Code')}
                        </Field>
                        <Field name="organizationName" component={Input}>
                          {t('Organization Name')}
                        </Field>
                        <Field name="address" component={Input}>
                          {t('Address')}
                        </Field>
                        <Field name="email" component={Input}>
                          {t('Email')}
                        </Field>
                      </div>
                      <div className="subcontractor-item-create--dialog--column">
                        <Field name="phone" component={Input}>
                          {t('Phone')}
                        </Field>
                        <div className="subcontractor-item-create--notes">
                          <Field
                            name="notes"
                            component={Textarea}
                            label={t('Notes')}
                          />
                        </div>
                        <div className="subcontractor-item-create--dialog--column--item">
                          <Field
                            name="isActive"
                            alignIndicator="right"
                            component={Switch}
                            checked={values.isActive}
                            label={`${t('Is Active')}`}
                            setFieldValue={setFieldValue}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="subcontractor-item-create--dialog--submit-button">
                      <RaisedButton type="submit" disabled={isSubmitting}>
                        {t('Save')}
                      </RaisedButton>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>
    );
  }
}

SubcontractorDialog.propTypes = {
  t: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  subcontractor: PropTypes.object,
  createSubcontractor: PropTypes.func,
  clearSubcontractor: PropTypes.func,
  getSubcontractor: PropTypes.func,
  editSubcontractor: PropTypes.func,
  onSuccess: PropTypes.func
};

const mapStateToProps = state => ({
  subcontractor: state.subcontractor.subcontractor
});

const mapDispatchToProps = {
  createSubcontractor,
  clearSubcontractor,
  getSubcontractor,
  editSubcontractor
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(SubcontractorDialog));
